/**
 * Server-wide API key administration.
 *
 * A key carries a bot identity and a set of permissions, not a channel list:
 * a ticked permission applies to every channel on the server, including
 * private channels the bot has never joined. That trade favours a small,
 * legible permission surface over per-channel scoping, so the "Create a key"
 * card says so plainly rather than letting an admin discover it later.
 */

import { useState } from 'react'
import type { FormEvent, ReactElement } from 'react'
import { useQueryClient } from '@tanstack/react-query'

import {
  queryKeys,
  uploadBotAvatar,
  useApiKeys,
  useCreateApiKey,
  useDeleteBotAvatar,
  useRevokeApiKey,
  useSetBotAvatarUrl,
  useUpdateApiKey,
} from '@/api/queries'
import type { ApiKey, ApiKeyPermission } from '@/api/types'
import { BotAvatarField } from '@/components/BotAvatarField'
import { Button, Checkbox, Modal, Select, Spinner, TextField, useToast } from '@/components/ui'

import { AdminPage, Card, ErrorNotice, Notice, useCopy } from './AdminLayout'
import styles from './ApiKeysPage.module.css'

/**
 * Said once, reused everywhere a permission is granted: creating a key and
 * broadening one that already exists are the same decision, and the warning
 * must read identically in both places.
 */
const PERMISSIONS_WARNING =
  'A permission applies to every channel on this server, including private channels the bot has never joined.'

const PERMISSION_LABELS: ReadonlyArray<readonly [ApiKeyPermission, string]> = [
  ['create_channels', 'Create channels'],
  ['edit_channels', 'Edit channels'],
  ['close_channels', 'Close (archive/reopen) channels'],
  ['manage_members', 'Add and remove channel members'],
  ['create_webhooks', 'Create and manage webhooks'],
  ['manage_widgets', 'Create and manage widgets'],
  ['post_messages', 'Post messages to a channel or a user'],
]

function labelFor(permission: string): string {
  return PERMISSION_LABELS.find(([value]) => value === permission)?.[1] ?? permission
}

function toggle(values: ApiKeyPermission[], value: ApiKeyPermission): ApiKeyPermission[] {
  return values.includes(value) ? values.filter((item) => item !== value) : [...values, value]
}

interface PermissionFieldsetProps {
  selected: ApiKeyPermission[]
  onToggle: (permission: ApiKeyPermission) => void
}

function PermissionFieldset({ selected, onToggle }: PermissionFieldsetProps): ReactElement {
  return (
    <fieldset className={styles.choices}>
      <legend>Permissions</legend>
      {PERMISSION_LABELS.map(([permission, label]) => (
        <Checkbox
          key={permission}
          label={label}
          checked={selected.includes(permission)}
          onChange={() => onToggle(permission)}
        />
      ))}
    </fieldset>
  )
}

interface EditKeyModalProps {
  editingKey: ApiKey
  onClose: () => void
}

function EditKeyModal({ editingKey, onClose }: EditKeyModalProps): ReactElement {
  const [name, setName] = useState(editingKey.name)
  const [botName, setBotName] = useState(editingKey.bot_name)
  const [permissions, setPermissions] = useState<ApiKeyPermission[]>(editingKey.permissions)
  const update = useUpdateApiKey()
  const deleteAvatar = useDeleteBotAvatar()
  const setAvatarUrl = useSetBotAvatarUrl()
  const queryClient = useQueryClient()
  const toast = useToast()

  // Uploading is not a mutation hook — see the note on `uploadBotAvatar` — so
  // this invalidates the key list itself, the same way `uploadLogo` in
  // SettingsPage.tsx writes the settings query back in after it resolves.
  const uploadAvatar = async (file: File, onProgress: (fraction: number) => void): Promise<string | null> => {
    const result = await uploadBotAvatar(editingKey.id, file, onProgress)
    void queryClient.invalidateQueries({ queryKey: queryKeys.admin.apiKeys() })
    return result.avatar_url
  }

  const removeAvatar = async (): Promise<string | null> => {
    const result = await deleteAvatar.mutateAsync(editingKey.id)
    return result.avatar_url
  }

  const setUrlAvatar = async (url: string): Promise<string | null> => {
    const result = await setAvatarUrl.mutateAsync({ apiKeyId: editingKey.id, url })
    return result.avatar_url
  }

  const save = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault()
    update.mutate(
      { id: editingKey.id, changes: { name, bot_name: botName, permissions } },
      {
        onSuccess: () => {
          toast.success('API key updated.')
          onClose()
        },
      },
    )
  }

  return (
    <Modal
      open
      onClose={() => !update.isPending && onClose()}
      title="Edit API key"
      size="md"
      closeOnOverlayClick={false}
    >
      <form className={styles.form} onSubmit={save}>
        <TextField
          label="Name"
          value={name}
          maxLength={80}
          required
          onChange={(event) => setName(event.target.value)}
        />
        <TextField
          label="Bot name"
          value={botName}
          maxLength={80}
          required
          onChange={(event) => setBotName(event.target.value)}
          hint="Shown when this key's bot posts a message or manages a channel."
        />
        <BotAvatarField
          name={botName.trim() === '' ? editingKey.bot_name : botName}
          currentUrl={editingKey.bot_avatar_url}
          onUpload={uploadAvatar}
          onRemove={removeAvatar}
          onSetUrl={setUrlAvatar}
          hint="Avatar changes are saved immediately and do not wait for Save."
        />
        <Notice tone="warning">{PERMISSIONS_WARNING}</Notice>
        <PermissionFieldset
          selected={permissions}
          onToggle={(permission) => setPermissions(toggle(permissions, permission))}
        />
        {update.error ? <ErrorNotice error={update.error} title="The key could not be updated" /> : null}
        <Button
          type="submit"
          variant="primary"
          loading={update.isPending}
          disabled={!name.trim() || !botName.trim() || permissions.length === 0}
        >
          Save
        </Button>
      </form>
    </Modal>
  )
}

export function ApiKeysPage(): ReactElement {
  const keys = useApiKeys()
  const [name, setName] = useState('')
  const [botName, setBotName] = useState('')
  const [days, setDays] = useState('90')
  const [permissions, setPermissions] = useState<ApiKeyPermission[]>(['post_messages'])
  const [secret, setSecret] = useState<string | null>(null)
  const [revoking, setRevoking] = useState<ApiKey | null>(null)
  const [editing, setEditing] = useState<ApiKey | null>(null)
  const copy = useCopy()
  const toast = useToast()
  const create = useCreateApiKey()
  const revoke = useRevokeApiKey()

  const closeSecret = (): void => {
    setSecret(null)
    create.reset()
  }

  const submitCreate = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault()
    create.mutate(
      {
        name,
        bot_name: botName,
        permissions,
        expires_at: new Date(Date.now() + Number(days) * 86_400_000).toISOString(),
      },
      {
        onSuccess: (issued) => {
          setSecret(issued.secret)
          setName('')
          setBotName('')
          setPermissions(['post_messages'])
        },
      },
    )
  }

  return (
    <AdminPage title="API keys" lead="Give external systems a bot identity and a set of permissions.">
      <Card title="Create a key" lead="The secret is shown once.">
        <form className={styles.form} onSubmit={submitCreate}>
          <TextField
            label="Name"
            value={name}
            maxLength={80}
            required
            onChange={(event) => setName(event.target.value)}
            placeholder="Operations dashboard"
          />
          <TextField
            label="Bot name"
            value={botName}
            maxLength={80}
            required
            onChange={(event) => setBotName(event.target.value)}
            placeholder="Ops Bot"
            hint="Shown when this key's bot posts a message or manages a channel."
          />
          <Select
            label="Expires after"
            value={days}
            onChange={(event) => setDays(event.target.value)}
            options={['7', '30', '90', '365'].map((value) => ({ value, label: `${value} days` }))}
          />
          <Notice tone="warning">{PERMISSIONS_WARNING}</Notice>
          <PermissionFieldset
            selected={permissions}
            onToggle={(permission) => setPermissions(toggle(permissions, permission))}
          />
          {create.error ? <ErrorNotice error={create.error} title="The key could not be created" /> : null}
          <Button
            type="submit"
            variant="primary"
            loading={create.isPending}
            disabled={!name.trim() || !botName.trim() || permissions.length === 0}
          >
            Create API key
          </Button>
        </form>
      </Card>
      <Card title="Issued keys">
        {keys.isPending ? (
          <Spinner label="Loading API keys" />
        ) : keys.error ? (
          <ErrorNotice error={keys.error} />
        ) : keys.data?.keys.length === 0 ? (
          <p>No API keys have been issued.</p>
        ) : (
          <ul className={styles.keys}>
            {keys.data?.keys.map((key) => {
              const expired = Date.parse(key.expires_at) <= Date.now()
              return (
                <li key={key.id}>
                  <div>
                    <strong>{key.name}</strong>
                    <p>{key.bot_name}</p>
                    <p>
                      <code>{key.secret_prefix}…</code> ·{' '}
                      {key.revoked_at
                        ? 'Revoked'
                        : expired
                          ? 'Expired'
                          : `Expires ${new Date(key.expires_at).toLocaleDateString()}`}
                    </p>
                    <p>
                      {key.permissions.length > 0 ? key.permissions.map(labelFor).join(' · ') : 'No permissions'}
                    </p>
                    <p>
                      {key.last_used_at
                        ? `Last used ${new Date(key.last_used_at).toLocaleString()}`
                        : 'Not used yet'}
                    </p>
                  </div>
                  {!key.revoked_at ? (
                    <div className={styles.actions}>
                      <Button size="sm" aria-label={`Edit ${key.name}`} onClick={() => setEditing(key)}>
                        Edit
                      </Button>
                      <Button
                        variant="danger"
                        size="sm"
                        aria-label={`Revoke ${key.name}`}
                        onClick={() => {
                          revoke.reset()
                          setRevoking(key)
                        }}
                      >
                        Revoke
                      </Button>
                    </div>
                  ) : null}
                </li>
              )
            })}
          </ul>
        )}
      </Card>
      <Modal
        open={secret !== null}
        onClose={closeSecret}
        title="Copy your API key"
        size="sm"
        closeOnOverlayClick={false}
      >
        <div className={styles.form}>
          <p>This secret cannot be shown again. Copy it before closing this window.</p>
          <TextField label="API key" value={secret ?? ''} readOnly spellCheck={false} autoComplete="off" />
          <Button variant="primary" onClick={() => secret && copy(secret, 'API key')}>
            Copy key
          </Button>
          <Button onClick={closeSecret}>Done</Button>
        </div>
      </Modal>
      <Modal
        open={revoking !== null}
        onClose={() => !revoke.isPending && setRevoking(null)}
        title="Revoke API key"
        size="sm"
      >
        <div className={styles.form}>
          <p>{revoking?.name} will immediately lose access. Past messages and widgets will remain.</p>
          {revoke.error ? <ErrorNotice error={revoke.error} /> : null}
          <Button
            variant="danger"
            loading={revoke.isPending}
            onClick={() => {
              if (revoking) {
                revoke.mutate(revoking.id, {
                  onSuccess: () => {
                    toast.success('API key revoked.')
                    setRevoking(null)
                  },
                })
              }
            }}
          >
            Revoke key
          </Button>
        </div>
      </Modal>
      {editing ? <EditKeyModal editingKey={editing} onClose={() => setEditing(null)} /> : null}
    </AdminPage>
  )
}
